<template>
  <div class="files-container">
    <div class="header">
      <TextBody i18n-key="files.descriptionHeader" tag="span" :body-type="3" />
      <BbBtn size="small" type="primary" color="#6222e3" @click="triggerUpload">
        <template #prepend>
          <BbIcon size="small" name="upgrade" />
        </template>
        <TextBody i18n-key="common.upload" />
      </BbBtn>
      <input type="file" ref="fileInput" :accept="acceptedFileTypes" @change="uploadFile" style="display: none" />
    </div>

    <div v-if="files.length" class="row-view">
      <div :class="['column-view', 'columnName']">{{ t('files.fileName') }}</div>
      <div :class="['column-view', 'columnName']">{{ t('files.date') }}</div>
      <div :class="['column-view', 'columnName']">{{ t('files.format') }}</div>
      <div :class="['column-view', 'columnName']">{{ t('files.size') }}</div>
      <div :class="['column-view', 'columnName']"></div>
    </div>

    <hr class="separator" />

    <div class="files-content" v-if="!isLoading">
      <div v-for="(file, index) in files" :key="`file-${index}`" class="file-wrapper">
        <div class="row-view">
          <div class="column-view">
            <span>{{ file.fileName }}</span>
          </div>
          <div class="column-view">
            <div class="column-view">
              <span>{{ formatTimestampToYearAndMonth(file.modifiedAt) }}</span>
            </div>
          </div>
          <div class="column-view">
            <span>{{ file.type }}</span>
          </div>
          <div class="column-view">
            <span>{{ file.size }}</span>
          </div>
          <div class="column-view">
            <BbBtn size="small" type="secondary" @click="openTranslatePopup(file.externalId, file.fileName)">
              <BbIcon size="small" name="translate" />
              <TextBody i18n-key="common.translate" />
            </BbBtn>
            <ActionsMenu :id="file.externalId" :actions-menu-items="actionsMenu" @remove="onRemoveFile"
              @download="downloadFile" />
          </div>
        </div>
        <hr class="separator" />
      </div>
    </div>
    <LoadingIndicator v-else class="loading-spinner" />
    <div v-if="!files.length && !isLoading" class="empty-state"></div>
  </div>
  <WarningPopup :popup-opened="fileTypeNotAcceptedPopupOpened" title-key="files.unsupportedFileType"
    warning-message-key="files.fileTypeNotAccepted" :warning-message2="`(${acceptedFileTypes})`"
    @update:open="fileTypeNotAcceptedPopupOpened = false" @execute:action="fileTypeNotAcceptedPopupOpened = false" />
</template>

<script>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { RESOURCE_ACTION_TYPE, RESOURCE_TYPE } from '~/constants/user';

import ActionsMenu from '~/components/common/ActionsMenu';
import LoadingIndicator from '~/components/common/LoadingIndicator';
import TextBody from '~/components/common/TextBody';
import useFiles from '~/composables/useFiles';
import useModals from '~/composables/useModals';
import useTranslation from '~/composables/useTranslation';
import useDocuments from '~/composables/useDocuments';
import { formatTimestampToYearAndMonth } from '~/utils/date-utils';
import WarningPopup from '~/components/common/WarningPopup.vue';
import { TRANSLATION_TOPIC_CHANNEL } from '@/constants/translations';

export default {
  props: {
    glossary: {
      type: Object,
      default: () => { },
    },
  },
  components: { WarningPopup, ActionsMenu, TextBody, LoadingIndicator },
  setup() {
    const { t } = useI18n();

    const acceptedFileTypes = '.pdf, .doc, .docx, .xlsx, .pptx, .srt, .xlf, .xliff, .html, .htm, .txt, .idml';

    const actionsMenu = [
      {
        name: 'edit',
        label: 'common.edit',
      },
      {
        name: 'download',
        label: 'common.download',
      },
      {
        name: 'delete',
        label: 'common.delete',
      },
    ];

    const files = ref([]);
    const fileInput = ref(null);
    const isLoading = ref(false);
    const fileTypeNotAcceptedPopupOpened = ref(false);
    const { getUserFiles, removeFile, uploadUserFile } = useFiles();
    const { showTranslationModal } = useModals();
    const { subscribeToDocumentTranslationChannel, unsubscribeFromDocumentTranslationChannel } = useTranslation();
    const { getPdfUrl } = useDocuments();

    const triggerUpload = () => fileInput.value.click();
    const openTranslatePopup = (externalId, fileName) => {
      const onTranslationStarted = async () => {
        const callback = async () => {
          isLoading.value = true;
          files.value = await getUserFiles();
          isLoading.value = false;
          unsubscribeFromDocumentTranslationChannel(externalId, TRANSLATION_TOPIC_CHANNEL.done, callback);
        };
        subscribeToDocumentTranslationChannel(externalId, TRANSLATION_TOPIC_CHANNEL.done, callback);
      };
      showTranslationModal(externalId, fileName, onTranslationStarted);
    };
    const downloadFile = async (id) => {
      const selectedFile = files.value.find((file) => file.externalId === id);
      const url = await getPdfUrl(selectedFile.externalId);
      window.open(url || '');
    };

    const uploadFile = async (event) => {
      const file = event.target.files[0];
      const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

      if (!acceptedFileTypes.split(', ').includes(fileExtension)) {
        fileTypeNotAcceptedPopupOpened.value = true;
        return; // Stop the upload process
      }

      isLoading.value = true;
      const uploadedFile = await uploadUserFile(file);
      if (uploadedFile) {
        files.value = await getUserFiles();
        isLoading.value = false;
      } else {
        // something went wrong
      }
    };

    const onRemoveFile = async (removedFileId) => {
      await removeFile(removedFileId);
      files.value = files.value.filter((file) => file.externalId !== removedFileId);
    };

    onMounted(async () => {
      isLoading.value = true;
      files.value = await getUserFiles();
      isLoading.value = false;
    });

    return {
      actionsMenu,
      acceptedFileTypes,
      downloadFile,
      formatTimestampToYearAndMonth,
      files,
      fileInput,
      isLoading,
      RESOURCE_ACTION_TYPE,
      RESOURCE_TYPE,
      onRemoveFile,
      openTranslatePopup,
      triggerUpload,
      uploadFile,
      t,
      fileTypeNotAcceptedPopupOpened,
    };
  },
};
</script>

<style scoped lang="scss">
.files-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background-color: $bb-neutral-0;
  padding: $bb-spacing-4;

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    justify-content: center;
    gap: $bb-spacing-2;

    .create-new-container {
      display: flex;
      flex-direction: column;
      gap: $bb-spacing-2;
    }

    span {
      font-size: $bb-spacing-6;
      line-height: $bb-spacing-7;
    }
  }

  .loading-spinner {
    background-color: $bb-neutral-0;
  }

  .header {
    display: flex;
    /* Use flexbox layout */
    justify-content: space-between;
    /* Align items at the beginning and the end */
    flex-wrap: wrap;
    /* Allow items to wrap to the next line if necessary */
    width: 100%;
    /* Adjust width as needed */
    padding: $bb-spacing-2;
    /* Optional padding */
    margin-bottom: $bb-spacing-5;

    :deep(span) {
      max-width: 500px;
    }
  }

  .files-content {
    .file-wrapper {
      padding-top: $bb-spacing-4;
      border-bottom: 1px solid $bb-neutral-5;
    }
  }

  .row-view {
    display: flex;
    align-items: center;
    gap: $bb-spacing-2;
  }

  .column-view {
    display: flex;
    align-items: center;
    flex: 20%;
    word-break: break-word;

    .public-container {
      display: flex;
      gap: 2px;
    }

    &.columnName {
      @include bb-medium-text-styles('md');
      color: $bb-neutral-2;
    }

    &:last-child {
      // flex: 0.1;
    }
  }

  .separator {
    margin-top: $bb-spacing-4;
    background: $bb-neutral-4;
    height: 1px;
    border: none;
  }
}
</style>
